import { useCallback } from 'react';
import { useGoalFormContext } from './GoalFormProvider';
import { SaveStatus } from './types';

type UseFieldPropsOptions = {
  autoSave?: (name: string, value: unknown) => Promise<void>;
  /** When true, auto-save triggers on onBlur instead of onChange. Use for text inputs. */
  saveOnBlur?: boolean;
};

type FieldProps<V = unknown> = {
  value: V;
  onChange: (value: V) => void;
  onBlur: () => void | Promise<void>;
  error: string | undefined;
  saveStatus: SaveStatus | undefined;
};

/**
 * Returns field-level props (value, onChange, onBlur, error, saveStatus) for a named form field.
 * The form state is read from the nearest GoalFormProvider context.
 *
 * The context stores a type-erased GoalFormApi (see GoalFormProvider), so field values
 * are not statically narrowed per field name. Consumers that need a specific type should
 * cast the returned `value` or use `useGoalFormContext<T>()` directly.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- GoalFormProvider erases the form type parameter; field values cannot be statically narrowed without a typed context
export function useFieldProps(
  name: string,
  options?: UseFieldPropsOptions,
): FieldProps<any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- context is type-erased (see GoalFormProvider)
  const form = useGoalFormContext<any>();

  const value = form.values[name];
  const error = form.errors[name];
  const saveStatus = form.saveStatuses[name];

  const triggerSave = useCallback(
    async (valueToSave: unknown) => {
      if (!options?.autoSave) return;
      form.setSaveStatus(name, { loading: true });
      try {
        await options.autoSave(name, valueToSave);
        form.setSaveStatus(name, { loading: false, completed: true });
      } catch {
        form.setSaveStatus(name, { loading: false, error: true });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable refs: setSaveStatus is memoized, name and autoSave are constant per call site
    [form.setSaveStatus, name, options?.autoSave],
  );

  const onChange = useCallback(
    (newValue: unknown) => {
      form.setFieldValue(name, newValue);
      // Auto-save immediately on change (default for selects, checkboxes, InputWithSave)
      if (options?.autoSave && !options?.saveOnBlur) {
        triggerSave(newValue);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable refs: setFieldValue is memoized, name/saveOnBlur/autoSave are constant per call site
    [
      form.setFieldValue,
      name,
      options?.autoSave,
      options?.saveOnBlur,
      triggerSave,
    ],
  );

  const onBlur = useCallback(async () => {
    const fieldError = form.validateField(name);
    // Auto-save on blur (for text inputs with saveOnBlur: true)
    if (
      options?.autoSave &&
      options?.saveOnBlur &&
      !fieldError &&
      form.isDirty(name)
    ) {
      await triggerSave(form.values[name]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form.values is included but changes on every keystroke; we intentionally read the latest value inside the callback
  }, [
    form.validateField,
    form.isDirty,
    form.values,
    name,
    options?.autoSave,
    options?.saveOnBlur,
    triggerSave,
  ]);

  return { value, onChange, onBlur, error, saveStatus };
}
